import { parseDay } from '@map-my-money/engine';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import type { McpConnection, Share } from '../api/types.ts';
import { BillingCard } from '../components/BillingCard.tsx';
import { RecentChanges } from '../components/cards.tsx';
import { Icon } from '../components/Icon.tsx';
import { mockOnly } from '../components/mockOnly.ts';
import { PresentationSetting } from '../components/PresentationSetting.tsx';
import { TimeZoneSetting } from '../components/TimeZoneSetting.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { Field, Modal, SectionHeader, Toggle } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { now, todayString } from '../lib/clock.ts';
import { fullDate, whenLabel } from '../lib/format.ts';
import { useIsDesktop, useLocalPreference } from '../lib/hooks.ts';
import { useActivePlan, usePlan, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

export function ConnectionsScreen() {
  useDocumentTitle(t('nav.connections'));
  const desktop = useIsDesktop();
  if (!desktop) return <MobileSettings />;
  return (
    <div className="page connect-desktop">
      <PageHeader eyebrow={t('connect.eyebrow')} title={t('connect.title')} subtitle={t('connect.subtitle')} />
      <div className="connect-desktop__grid">
        <div className="stack-lg">
          <AiAssistants />
          <SharedLinks />
        </div>
        <div className="stack-lg">
          <History titleId="history-title" className="card--grow" />
          <Account />
        </div>
      </div>
    </div>
  );
}

function DemoData() {
  const { DemoDataPicker } = mockOnly;
  return DemoDataPicker ? <DemoDataPicker /> : null;
}

export function SettingsScreen() {
  useDocumentTitle(t('nav.settings'));
  const desktop = useIsDesktop();
  if (!desktop) return <MobileSettings />;
  return (
    <div className="page settings-desktop">
      <PageHeader eyebrow={t('settings.eyebrow')} title={t('settings.title')} />
      <div className="settings-desktop__grid">
        <div className="stack-lg">
          <PlanSettings />
          <PresentationSetting />
          <TimeZoneSetting />
          <DemoData />
        </div>
        <div className="stack-lg">
          <BillingCard />
          <Account />
        </div>
      </div>
    </div>
  );
}

function MobileSettings() {
  return (
    <div className="page page--mobile">
      <header className="mobile-header">
        <h1 className="mobile-title">{t('settings.title')}</h1>
      </header>
      <PlansCard />
      <PlanSettings />
      <AiAssistants />
      <PresentationSetting />
      <TimeZoneSetting />
      <SharedLinks />
      <History titleId="m-history-title" />
      <BillingCard />
      <Account />
      <DemoData />
    </div>
  );
}

/** The change history; `?since=N` (from a chat link or the assistant activity) shows only what came after version N. */
function History({ titleId, className = '' }: { titleId: string; className?: string }) {
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const raw = params.get('since');
  const since = raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    if (hash === '#history' || since !== null) section.current?.scrollIntoView({ block: 'start' });
  }, [hash, since]);
  const showAll = () => {
    params.delete('since');
    setParams(params, { replace: true });
  };
  return (
    <section ref={section} id="history" className={`card card--pad-lg history ${className}`} aria-labelledby={titleId}>
      <SectionHeader title={t('connect.history')} id={titleId} />
      {since !== null && (
        <p className="history__filter" role="status">
          {t('history.since', { version: since })}{' '}
          <button type="button" className="link-button" onClick={showAll}>{t('history.showAll')}</button>
        </p>
      )}
      <RecentChanges showAll since={since} />
    </section>
  );
}

type McpState =
  | { status: 'loading' }
  | { status: 'ready'; connections: readonly McpConnection[] }
  | { status: 'unavailable' }
  | { status: 'error' };

const KNOWN_ASSISTANTS = [
  { key: 'claude', name: 'Claude', match: /claude|anthropic/i },
  { key: 'chatgpt', name: 'ChatGPT', match: /chatgpt|openai/i },
] as const;

const assistantTone = (name: string) => (/claude|anthropic/i.test(name) ? 'claude' : 'neutral');

const localDay = (iso: string) => {
  const at = new Date(iso);
  return fullDate(parseDay(`${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`));
};

function AiAssistants() {
  const [state, setState] = useState<McpState>({ status: 'loading' });
  const [confirming, setConfirming] = useState<McpConnection | null>(null);
  const [reload, setReload] = useState(0);
  const toast = useToast();
  const id = useId();
  const urlId = useId();
  const [connectorUrl, setConnectorUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    api.authMethods().then(
      (methods) => live && setConnectorUrl(methods.mcpUrl ?? null),
      () => live && setConnectorUrl(null),
    );
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    api.mcpConnections().then(
      (r) => live && setState({ status: 'ready', connections: r.connections }),
      (e) => live && setState({ status: e instanceof ApiError && e.status === 404 ? 'unavailable' : 'error' }),
    );
    return () => {
      live = false;
    };
  }, [reload]);
  const connections = state.status === 'ready' ? state.connections : [];
  const missing = state.status === 'error' ? [] : KNOWN_ASSISTANTS.filter((a) => !connections.some((c) => a.match.test(c.clientName)));
  const copyUrl = (message: string) => {
    if (connectorUrl) void navigator.clipboard?.writeText(connectorUrl).then(() => toast.push({ tone: 'success', message }));
  };
  const disconnect = async (connection: McpConnection) => {
    if (state.status !== 'ready') return;
    setConfirming(null);
    setState({ status: 'ready', connections: connections.filter((c) => c.id !== connection.id) });
    try {
      await api.disconnectMcp(connection.id);
      toast.push({ tone: 'success', message: t('connect.ai.disconnected', { name: connection.clientName }) });
    } catch {
      setState({ status: 'ready', connections });
      toast.push({ tone: 'error', message: t('error.saveFailed') });
    }
  };
  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id} aria-busy={state.status === 'loading'}>
      <SectionHeader title={t('connect.ai')} id={id} />
      <ul className="stack-sm">
        {connections.map((c) => (
          <li key={c.id} className="assistant">
            <span className={`assistant__mark assistant__mark--${assistantTone(c.clientName)}`} aria-hidden="true">
              {/chatgpt|openai/i.test(c.clientName) ? 'G' : c.clientName.charAt(0).toUpperCase()}
            </span>
            <span className="assistant__text">
              <span className="assistant__name">
                {c.clientName}
                {c.scopes.includes('plan:write') ? (
                  <span className="source-badge source-badge--write">{t('connect.ai.canChange')}</span>
                ) : (
                  <span className="source-badge">{t('connect.ai.readOnly')}</span>
                )}
              </span>
              <span className="assistant__detail">
                {t('connect.ai.since', { date: localDay(c.createdAt) })} · {t('connect.ai.endsOn', { date: localDay(c.expiresAt) })}
              </span>
              <span className="assistant__meta">
                {c.lastUsedAt ? t('connect.ai.lastUsed', { when: whenLabel(c.lastUsedAt, now()).toLowerCase() }) : t('connect.ai.neverUsed')}
              </span>
            </span>
            <span className="assistant__actions">
              <button type="button" className="button button--sm button--outline-danger" onClick={() => setConfirming(c)} aria-label={t('connect.ai.disconnectLabel', { name: c.clientName })}>
                {t('connect.ai.disconnect')}
              </button>
            </span>
          </li>
        ))}
        {missing.map((a) => (
          <li key={a.key} className="assistant">
            <span className={`assistant__mark assistant__mark--${a.key === 'claude' ? 'claude' : 'neutral'}`} aria-hidden="true">
              {a.key === 'claude' ? 'C' : <Icon name="plusCircle" size={18} />}
            </span>
            <span className="assistant__text">
              <span className="assistant__name">{a.name}</span>
              <span className="assistant__detail">{state.status === 'unavailable' ? t('connect.ai.unavailable') : t('connect.ai.notConnected')}</span>
            </span>
            <button
              type="button"
              className="button button--sm button--primary"
              disabled={state.status !== 'ready' || !connectorUrl}
              onClick={() => copyUrl(t('connect.ai.copiedHint'))}
              aria-label={t('connect.ai.connectLabel', { name: a.name })}
            >
              {t('connect.ai.connect')}
            </button>
          </li>
        ))}
      </ul>
      {state.status === 'error' && (
        <div className="assistant assistant--problem" role="alert">
          <span className="assistant__text">
            <span className="assistant__detail">{t('connect.ai.loadFailed')}</span>
          </span>
          <button type="button" className="button button--sm button--outline" onClick={() => setReload((n) => n + 1)}>
            {t('common.retry')}
          </button>
        </div>
      )}
      {confirming && <DisconnectDialog connection={confirming} onClose={() => setConfirming(null)} onConfirm={() => void disconnect(confirming)} />}
      <Field label={t('connect.ai.url')} htmlFor={urlId} hint={connectorUrl === null ? t('connect.ai.urlUnavailable') : t('connect.ai.urlHint')}>
        <div className="copy-field">
          <input id={urlId} className="input input--mono" readOnly value={connectorUrl ?? ''} aria-busy={connectorUrl === undefined} onFocus={(e) => e.target.select()} />
          <button type="button" className="button button--outline" disabled={!connectorUrl} onClick={() => copyUrl(t('common.copied'))}>
            {t('common.copy')}
          </button>
        </div>
      </Field>
    </section>
  );
}

function DisconnectDialog({ connection, onClose, onConfirm }: { connection: McpConnection; onClose: () => void; onConfirm: () => void }) {
  const bodyId = useId();
  return (
    <Modal
      title={t('connect.ai.disconnectTitle', { name: connection.clientName })}
      onClose={onClose}
      describedBy={bodyId}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--danger" onClick={onConfirm}>{t('connect.ai.disconnect')}</button>
        </>
      }
    >
      <p id={bodyId} className="modal__text">{t('connect.ai.disconnectBody', { name: connection.clientName })}</p>
    </Modal>
  );
}

function shareStatus(share: Share, at: Date): { views: string; expires: string; live: boolean } {
  const views = share.views === 0 ? t('connect.shares.viewsNone') : share.views === 1 ? t('connect.shares.viewsOne') : t('connect.shares.views', { n: share.views });
  if (share.revokedAt) return { views, expires: t('connect.shares.revoked'), live: false };
  if (!share.expiresAt) return { views, expires: t('connect.shares.never'), live: true };
  const days = Math.ceil((new Date(share.expiresAt).getTime() - at.getTime()) / 86_400_000);
  if (days <= 0) return { views, expires: t('connect.shares.expired'), live: false };
  return { views, expires: t('connect.shares.inDays', { n: days }), live: true };
}

function SharedLinks() {
  const { shares, setShares } = useStore();
  const desktop = useIsDesktop();
  const toast = useToast();
  const navigate = useNavigate();
  const [showAmounts, setShowAmounts] = useLocalPreference('mm.share.showAmounts', false);
  const id = useId();
  const at = now();
  const revoke = async (share: Share) => {
    setShares((all) => all.map((s) => (s.token === share.token ? { ...s, revokedAt: at.toISOString() } : s)));
    try {
      await api.revokeShare(share.token);
    } catch {
      setShares((all) => all.map((s) => (s.token === share.token ? share : s)));
      toast.push({ tone: 'error', message: t('error.saveFailed') });
    }
  };
  return (
    <section className="card card--pad-lg stack-md card--grow" aria-labelledby={id}>
      <SectionHeader
        title={t('connect.shares')}
        id={id}
        action={<Toggle checked={showAmounts} onChange={setShowAmounts} label={t('whatIf.share.showAmounts')} visibleLabel={<span className="muted-2">{t('whatIf.share.showAmounts')}</span>} />}
      />
      {shares.length === 0 ? (
        <p className="muted">{t('connect.shares.none')}</p>
      ) : desktop ? (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('connect.shares.col.link')}</th>
                <th scope="col">{t('connect.shares.col.amounts')}</th>
                <th scope="col">{t('connect.shares.col.views')}</th>
                <th scope="col">{t('connect.shares.col.expires')}</th>
                <th scope="col" className="num"><span className="sr-only">{t('connect.shares.col.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => {
                const status = shareStatus(share, at);
                return (
                  <tr key={share.token} className={status.live ? '' : 'row--dim'}>
                    <th scope="row">
                      {status.live ? <Link to={`/s/${share.token}`} className="table-link">{share.title}</Link> : share.title}
                    </th>
                    <td className={share.showAmounts ? 'text-cyber' : 'muted'}>{share.showAmounts ? t('connect.shares.shown') : t('connect.shares.hidden')}</td>
                    <td className="muted-2">{status.views}</td>
                    <td className="muted-2">{status.expires}</td>
                    <td className="num">
                      {status.live && (
                        <button type="button" className="link-button link-button--danger" aria-label={t('connect.shares.revokeLabel', { name: share.title })} onClick={() => void revoke(share)}>
                          {t('connect.shares.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="share-list">
          {shares.map((share) => {
            const status = shareStatus(share, at);
            return (
              <li key={share.token} className={`share-list__item ${status.live ? '' : 'row--dim'}`}>
                <span className="share-list__text">
                  {status.live ? <Link to={`/s/${share.token}`} className="table-link">{share.title}</Link> : <span className="strong">{share.title}</span>}
                  <span className="share-list__meta">
                    <span className={share.showAmounts ? 'text-cyber' : ''}>{share.showAmounts ? t('connect.shares.shown') : t('connect.shares.hidden')}</span> · {status.views} · {status.expires}
                  </span>
                </span>
                {status.live && (
                  <button type="button" className="link-button link-button--danger" aria-label={t('connect.shares.revokeLabel', { name: share.title })} onClick={() => void revoke(share)}>
                    {t('connect.shares.revoke')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className="button button--dashed" onClick={() => navigate('/share')}>
        <Icon name="plus" size={14} strokeWidth={2.5} />
        {t('connect.shares.create')}
      </button>
    </section>
  );
}

function Account() {
  const { me, plan, versions, waypoints, shares, signOut, signedOut } = useStore();
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const id = useId();
  const exportData = () => {
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), user: me?.user, plan, versions, waypoints, shares }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'map-my-money-export.json';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <SectionHeader title={t('connect.account')} id={id} />
      <div className="account-row">
        <span className="account-row__icon" aria-hidden="true"><Icon name="apple" size={18} /></span>
        <span className="account-row__text">
          <span className="strong">{me?.user.email ? t('connect.account.email', { email: me.user.email }) : t('connect.account.apple')}</span>
          <span className="user-chip__badge">{t('app.privateAlpha')}</span>
        </span>
      </div>
      <div className="button-row">
        <button type="button" className="button button--outline button--flex" onClick={exportData}>
          <Icon name="download" size={15} />{t('connect.account.export')}
        </button>
        <button type="button" className="button button--outline button--flex" onClick={() => void signOut().then(() => navigate('/signin'))}>
          <Icon name="logout" size={15} />{t('common.signOut')}
        </button>
      </div>
      <button type="button" className="button button--outline-danger" onClick={() => setDeleting(true)}>{t('connect.account.delete')}</button>
      <p className="fine-print">{t('connect.account.note')}</p>
      {deleting && <DeleteDialog onClose={() => setDeleting(false)} onDeleted={() => { signedOut(); navigate('/signin'); }} />}
    </section>
  );
}

function DeleteDialog({ onClose, onDeleted }: { onClose: () => void; onDeleted: () => void }) {
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const id = useId();
  const bodyId = useId();
  const confirm = async () => {
    setBusy(true);
    try {
      await api.deleteMe();
      onDeleted();
    } catch (e) {
      toast.push({ tone: 'error', message: e instanceof ApiError ? e.message : t('error.saveFailed') });
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('connect.account.delete')}
      onClose={onClose}
      describedBy={bodyId}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="button button--danger" disabled={word !== t('connect.account.deleteWord') || busy} onClick={() => void confirm()}>{t('common.delete')}</button>
        </>
      }
    >
      <p id={bodyId} className="modal__text">{t('connect.account.deleteConfirm')}</p>
      <Field label={t('connect.account.deleteField')} htmlFor={id}>
        <input id={id} className="input" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" />
      </Field>
    </Modal>
  );
}

function PlanSettings() {
  const wire = usePlan();
  const { savePlan, revision } = useStore();
  const { canEdit } = useActivePlan();
  return <PlanSettingsForm key={revision} wire={wire} readOnly={!canEdit} onSave={(next) => savePlan(next, t('plan.summaryText.settings'))} />;
}

/** Phones have no sidebar: plans are reached from Settings. */
function PlansCard() {
  const { plans } = useStore();
  const { plan } = useActivePlan();
  const id = useId();
  if (!plan) return null;
  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <SectionHeader title={t('plans.settings.title')} id={id} />
      <p className="muted-2">{plans.length > 1 ? t('plans.settings.many', { n: plans.length, name: plan.name }) : t('plans.settings.one')}</p>
      <div className="button-row">
        <Link to="/plans?new=1" className="button button--outline button--flex"><Icon name="plus" size={15} strokeWidth={2.4} />{t('plans.new')}</Link>
        <Link to="/plans" className="button button--outline button--flex"><Icon name="plans" size={15} />{t('plans.switcher.manage')}</Link>
      </div>
    </section>
  );
}

function PlanSettingsForm({ wire, onSave, readOnly }: { wire: ReturnType<typeof usePlan>; onSave: (next: ReturnType<typeof usePlan>) => Promise<boolean>; readOnly: boolean }) {
  const c = wire.configuration;
  const [values, setValues] = useState({
    initialBalance: c.initialBalance, expectedReturnPercent: c.expectedReturnPercent,
    safeWithdrawalRatePercent: c.safeWithdrawalRatePercent, timeframeYears: String(c.timeframeYears),
  });
  const [busy, setBusy] = useState(false);
  const id = useId();
  const invalid = Object.entries(values).filter(([, v]) => v.trim() === '' || !Number.isFinite(Number(v)) || Number(v) < 0).map(([k]) => k);
  const years = Number(values.timeframeYears);
  const yearsInvalid = !Number.isInteger(years) || years < 1 || years > 50;
  const dirty = values.initialBalance !== c.initialBalance || values.expectedReturnPercent !== c.expectedReturnPercent
    || values.safeWithdrawalRatePercent !== c.safeWithdrawalRatePercent || values.timeframeYears !== String(c.timeframeYears);
  const fields: { key: keyof typeof values; label: 'settings.startingBalance' | 'settings.expectedReturn' | 'settings.withdrawalRate' | 'settings.timeframe' }[] = [
    { key: 'initialBalance', label: 'settings.startingBalance' },
    { key: 'expectedReturnPercent', label: 'settings.expectedReturn' },
    { key: 'safeWithdrawalRatePercent', label: 'settings.withdrawalRate' },
    { key: 'timeframeYears', label: 'settings.timeframe' },
  ];
  // The starting cash is true on a known day (R17): entering a new balance makes it today's cash.
  const balanceChanged = Number(values.initialBalance) !== Number(c.initialBalance);
  const balanceDate = balanceChanged ? todayString() : c.initialBalanceDate;
  const balanceHint = balanceDate
    ? t('settings.startingBalance.asOf', { date: fullDate(parseDay(balanceDate)) })
    : t('settings.startingBalance.monthStart');
  const submit = async () => {
    if (invalid.length > 0 || yearsInvalid) return;
    setBusy(true);
    await onSave({
      ...wire,
      configuration: {
        ...c,
        initialBalance: String(Number(values.initialBalance)),
        ...(balanceChanged ? { initialBalanceDate: todayString() } : {}),
        expectedReturnPercent: String(Number(values.expectedReturnPercent)),
        safeWithdrawalRatePercent: String(Number(values.safeWithdrawalRatePercent)),
        timeframeYears: years,
      },
    });
    setBusy(false);
  };
  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <SectionHeader title={t('settings.plan')} id={id} />
      <form className="settings-form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        {fields.map((f) => (
          <Field
            key={f.key}
            label={t(f.label)}
            htmlFor={`${id}-${f.key}`}
            {...(f.key === 'initialBalance' ? { hint: balanceHint } : {})}
            error={invalid.includes(f.key) || (f.key === 'timeframeYears' && yearsInvalid) ? t('plan.field.number') : null}
          >
            <input
              id={`${id}-${f.key}`}
              className="input"
              inputMode="decimal"
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
          </Field>
        ))}
        <div className="settings-form__actions">
          <button type="submit" className="button button--primary" disabled={readOnly || !dirty || busy || invalid.length > 0 || yearsInvalid}>{t('common.saveChanges')}</button>
        </div>
      </form>
    </section>
  );
}
